import { Fragment, useEffect, useReducer, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

export default function TimelineFilterDialog(props) {
    const itemLabel = props.itemLabel || "timeline";
    const [, forceUpdate] = useReducer((value) => value + 1, 0);
    const [codes, setCodes] = useState([]);
    const [groups, setGroups] = useState([]);
    const [ratings, setRatings] = useState([]);

    useEffect(() => {
        if (!props.rows) {
            return;
        }

        const codesArray = [];
        let groupsArray = props.groupsAfterFiltering;

        props.rows.forEach((row) => {
            if (!codesArray.some((code) => code.code === row.name)) {
                codesArray.push({
                    code: row.name,
                    selected: !row.filteredOutByCode,
                });
            }

            if (row.labels.length === 0) {
                let matchingGroup = groupsArray.find(
                    (group) => group.group === "(No Group)",
                );

                if (matchingGroup) {
                    if (
                        !matchingGroup.labels.some(
                            (label) => label.text === "(No Detail)",
                        )
                    ) {
                        matchingGroup.labels = [
                            { text: "(No Detail)", selected: true },
                            ...matchingGroup.labels,
                        ];
                    }
                } else {
                    groupsArray = [
                        {
                            group: "(No Group)",
                            labels: [
                                { text: "(No Detail)", selected: true },
                            ],
                            selected: true,
                        },
                        ...groupsArray,
                    ];
                }
                return;
            }

            row.labels.forEach((label) => {
                const groupName = label.group || "(No Group)";
                const matchingGroup = groupsArray.find(
                    (group) => group.group === groupName,
                );

                if (matchingGroup) {
                    if (
                        !matchingGroup.labels.some(
                            (groupLabel) => groupLabel.text === label.text,
                        )
                    ) {
                        matchingGroup.labels.push({
                            text: label.text,
                            selected: true,
                        });
                    }
                } else {
                    const newGroup = {
                        group: groupName,
                        labels: [{ text: label.text, selected: true }],
                        selected: true,
                    };
                    groupsArray =
                        groupName === "(No Group)"
                            ? [newGroup, ...groupsArray]
                            : [...groupsArray, newGroup];
                }
            });
        });

        codesArray.sort((a, b) =>
            a.code.toLowerCase() > b.code.toLowerCase() ? 1 : -1,
        );
        setCodes(codesArray);
        setGroups(groupsArray);
        setRatings(props.ratings);
    }, [props]);

    function handleClose() {
        const filteredCodes = codes
            .filter((code) => code.selected)
            .map((code) => code.code);
        const filteredLabels = [];
        groups.forEach((group) => {
            group.labels.forEach((label) => {
                if (label.selected) {
                    filteredLabels.push(label.text);
                }
            });
        });
        const filteredRatings = ratings
            .filter((rating) => rating.selected)
            .map((rating) => rating.stars);

        props.onClose(
            filteredCodes,
            filteredLabels,
            filteredRatings,
            groups,
        );
    }

    const handleToggle = (object) => () => {
        object.selected = !object.selected;
        if (object.group) {
            object.labels.forEach((label) => {
                label.selected = object.selected;
            });
        }
        forceUpdate();
    };

    const handleToggleAll = (array) => () => {
        const firstObject = array[0];
        if (!firstObject) {
            return;
        }

        const newSelected = !firstObject.selected;
        array.forEach((object) => {
            object.selected = newSelected;
            if (object.group) {
                object.labels.forEach((label) => {
                    label.selected = newSelected;
                });
            }
        });
        forceUpdate();
    };

    function renderStars(stars) {
        return (
            <Box sx={{ display: "flex", color: "primary.main" }}>
                {[0, 1, 2].map((starIndex) =>
                    starIndex < stars ? (
                        <StarIcon key={starIndex} fontSize="small" />
                    ) : (
                        <StarBorderIcon
                            key={starIndex}
                            fontSize="small"
                        />
                    ),
                )}
            </Box>
        );
    }

    function sectionHeader(title, array) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        {title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {array.filter((item) => item.selected).length} selected
                    </Typography>
                </Box>
                <Tooltip title="Toggle all">
                    <IconButton
                        onClick={handleToggleAll(array)}
                        aria-label={`Toggle all ${title.toLowerCase()}`}
                        size="small"
                    >
                        <DoneAllIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        );
    }

    return (
        <Dialog
            open={props.open}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
            aria-labelledby={`${itemLabel}-filter-dialog-title`}
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                        maxHeight: "92vh",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    <FilterAltOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        id={`${itemLabel}-filter-dialog-title`}
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.15 }}
                    >
                        Filter {itemLabel}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Choose which codes, details and ratings remain visible.
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Apply filters and close"
                    onClick={handleClose}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                            overflow: "hidden",
                        }}
                    >
                        {sectionHeader("Codes", codes)}
                        <List
                            disablePadding
                            sx={{ height: 390, overflowY: "auto" }}
                        >
                            {codes.map((code, index) => (
                                <Fragment key={code.code}>
                                    <ListItemButton
                                        dense
                                        onClick={handleToggle(code)}
                                    >
                                        <Checkbox
                                            edge="start"
                                            checked={code.selected}
                                            tabIndex={-1}
                                            disableRipple
                                        />
                                        <ListItemText primary={code.code} />
                                    </ListItemButton>
                                    {index < codes.length - 1 && <Divider />}
                                </Fragment>
                            ))}
                        </List>
                    </Box>

                    <Box
                        sx={{
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                            overflow: "hidden",
                        }}
                    >
                        {sectionHeader("Details", groups)}
                        <List
                            disablePadding
                            sx={{ height: 390, overflowY: "auto" }}
                        >
                            {groups.map((group, groupIndex) => (
                                <Fragment key={group.group}>
                                    <ListItemButton
                                        dense
                                        onClick={handleToggle(group)}
                                        sx={{ fontWeight: 800 }}
                                    >
                                        <Checkbox
                                            edge="start"
                                            checked={group.selected}
                                            tabIndex={-1}
                                            disableRipple
                                        />
                                        <ListItemText
                                            primary={group.group}
                                            primaryTypographyProps={{
                                                fontWeight: 800,
                                            }}
                                        />
                                    </ListItemButton>
                                    {group.labels.map((label) => (
                                        <ListItemButton
                                            key={label.text}
                                            dense
                                            onClick={handleToggle(label)}
                                            sx={{ pl: 4.5 }}
                                        >
                                            <Checkbox
                                                edge="start"
                                                checked={label.selected}
                                                tabIndex={-1}
                                                disableRipple
                                            />
                                            <ListItemText
                                                primary={label.text}
                                            />
                                        </ListItemButton>
                                    ))}
                                    {groupIndex < groups.length - 1 && (
                                        <Divider />
                                    )}
                                </Fragment>
                            ))}
                        </List>
                    </Box>

                    <Box
                        sx={{
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                            overflow: "hidden",
                        }}
                    >
                        {sectionHeader("Ratings", ratings)}
                        <List disablePadding>
                            {ratings.map((rating, index) => (
                                <Fragment key={rating.stars}>
                                    <ListItemButton
                                        dense
                                        onClick={handleToggle(rating)}
                                    >
                                        <Checkbox
                                            edge="start"
                                            checked={rating.selected}
                                            tabIndex={-1}
                                            disableRipple
                                        />
                                        {renderStars(rating.stars)}
                                    </ListItemButton>
                                    {index < ratings.length - 1 && (
                                        <Divider />
                                    )}
                                </Fragment>
                            ))}
                        </List>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={handleClose}
                >
                    Apply filters
                </Button>
            </DialogActions>
        </Dialog>
    );
}
